import React, { useEffect, useRef, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpRightAndDownLeftFromCenter } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Extension } from "@tiptap/core";
import {
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
} from "@tiptap/react";

import {
  LibraryImageNode as CoreLibraryImageNode,
  LibraryImageAttributes,
} from "@nnr/tiptap-extensions";
import { useDocumentFileAction } from "@/action/use-document-file-action";
import { useArtifactUuid } from "@/hooks/use-artifact-uuid";

const MAX_DOWNLOAD_RETRIES = 5;

interface LibraryImageProps extends Omit<NodeViewProps, "node"> {
  node: {
    attrs: LibraryImageAttributes;
  } & NodeViewProps["node"];
  selected: boolean;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    libraryImage: {
      setLibraryImage: (options: Partial<LibraryImageAttributes>) => ReturnType;
    };
  }
}

const LibraryImage: React.FC<LibraryImageProps> = ({
  node,
  selected,
  updateAttributes,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState<{
    width: string;
    height: string;
  }>({
    width: node.attrs.width || "100%",
    height: node.attrs.height || "auto",
  });
  const documentUuid = useArtifactUuid("documentUuid");
  const { getDownloadUrl } = useDocumentFileAction();

  const imageRef = useRef<HTMLImageElement>(null);
  const initialDimensionsRef = useRef<{
    width: number;
    height: number;
    aspectRatio: number;
  }>({ width: 0, height: 0, aspectRatio: 1 });
  const initialMousePosRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const fileUuid = node.attrs.src;

  useEffect(() => {
    if (!fileUuid || !documentUuid) {
      setError(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryDelay = 2000;
    let retries = 0;

    setLoading(true);
    setError(false);

    const loadImage = async () => {
      try {
        const url = await getDownloadUrl(documentUuid, fileUuid);
        if (cancelled) return;
        if (!url) {
          throw new Error("Library file URL not found");
        }
        setImageSrc(url);
        setError(false);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setError(true);
        setLoading(false);
        if (
          err &&
          typeof err === "object" &&
          "statusCode" in err &&
          err.statusCode === 404 &&
          retries < MAX_DOWNLOAD_RETRIES
        ) {
          retries += 1;
          retryTimer = setTimeout(() => void loadImage(), retryDelay);
          retryDelay = Math.min(retryDelay * 2, 30000);
        } else {
          console.error("Error loading library image:", err);
        }
      }
    };

    void loadImage();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
    };
  }, [documentUuid, fileUuid]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      e.preventDefault();

      const dx = e.clientX - initialMousePosRef.current.x;
      const newWidth = initialDimensionsRef.current.width + dx;
      const newHeight = newWidth / initialDimensionsRef.current.aspectRatio;

      setDimensions({
        width: `${newWidth}px`,
        height: `${newHeight}px`,
      });
    };

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false);
        updateAttributes({
          width: dimensions.width,
          height: dimensions.height,
        });
      }
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, updateAttributes, dimensions]);

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      initialDimensionsRef.current = {
        width: rect.width,
        height: rect.height,
        aspectRatio: rect.width / rect.height,
      };
      initialMousePosRef.current = { x: e.clientX, y: e.clientY };
      setIsResizing(true);
    }
  };

  if (loading) {
    return (
      <NodeViewWrapper>
        <div className="image-loading bg-background-2 flex h-12 items-center justify-center rounded">
          Loading...
        </div>
      </NodeViewWrapper>
    );
  }

  if (error) {
    return (
      <NodeViewWrapper>
        <div className="image-error flex h-12 items-center justify-center rounded bg-red-50 text-red-600">
          Error loading image
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper>
      <div
        className="relative"
        style={{ width: dimensions.width, height: dimensions.height }}
      >
        <img
          ref={imageRef}
          src={imageSrc ?? undefined}
          alt={node.attrs.alt ?? undefined}
          title={node.attrs.title ?? undefined}
          className="rounded-lg"
          style={{
            boxSizing: "border-box",
            border: selected ? "2px solid black" : "2px solid transparent",
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
          data-library-file-uuid={fileUuid}
          data-drag-handle
          draggable="true"
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
          }}
        />
        {selected && (
          <div
            onMouseDown={handleResizeStart}
            className="bg-foreground absolute right-2 bottom-2 z-10 flex h-4 w-4 rotate-90 cursor-nwse-resize items-center justify-center rounded text-white"
          >
            <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} size="xs" />
          </div>
        )}
      </div>
    </NodeViewWrapper>
  );
};

export const LibraryImageNodeWithView = CoreLibraryImageNode.extend<Extension>({
  addNodeView() {
    return ReactNodeViewRenderer(
      // @ts-ignore React type compatibility varies between local and CI environments
      LibraryImage as React.FC<NodeViewProps>,
    );
  },
});
